import {
  Component,
  forwardRef,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  ViewChild,
  OnDestroy,
  HostBinding,
  OnInit,
  AfterViewInit,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs/internal/Subject';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { filter } from 'rxjs/internal/operators/filter';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';

export interface DropdownOption {
  [key: string]: any;
}

@Component({
  selector: 'app-ep-form-select-dropdown',
  templateUrl: './ep-form-select-dropdown.component.html',
  styleUrls: ['./ep-form-select-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EpFormSelectDropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EpFormSelectDropdownComponent implements ControlValueAccessor, OnDestroy, OnInit, AfterViewInit, OnChanges {
  protected dropdownComponentDestroy$ = new Subject<void>();
  private static currentlyOpenDropdown: EpFormSelectDropdownComponent | null = null;

  @Input() public options: DropdownOption[] = [];
  @Input() public multiSelect = false;
  @Input() public valueKey?: string;
  @Input() public labelKey = '';
  @Input() public placeholder = '';
  @Input() public searchPlaceholder = '';

  @Output() public selectionChanged = new EventEmitter<any>();

  @ViewChild('searchInput') public searchInputEl!: ElementRef<HTMLInputElement>;

  @HostBinding('class.is-active')
  public get isActive(): boolean {
    return this.dropdownOpen;
  }

  public searchInput$ = new Subject<string>();
  public dropdownOpen = false;
  public searchTerm = '';
  public selectedOption: DropdownOption | null = null;
  public selectedOptions: DropdownOption[] = [];
  public filteredOptions: DropdownOption[] = [];

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private modelValue: any = null;

  constructor(private cdr: ChangeDetectorRef, private elRef: ElementRef) {}

  public ngOnInit(): void {
    this.searchInput$.pipe(takeUntil(this.dropdownComponentDestroy$), distinctUntilChanged()).subscribe((term: string) => {
      this.searchTerm = term;
      this.updateFilteredOptions();
    });
    this.updateFilteredOptions();
  }

  public ngOnDestroy(): void {
    this.dropdownComponentDestroy$.next();
    this.dropdownComponentDestroy$.complete();
    if (EpFormSelectDropdownComponent.currentlyOpenDropdown === this) {
      EpFormSelectDropdownComponent.currentlyOpenDropdown = null;
    }
  }

  public ngAfterViewInit(): void {
    fromEvent(document, 'click')
      .pipe(
        takeUntil(this.dropdownComponentDestroy$),
        filter(() => this.dropdownOpen),
        filter((event: Event) => !this.elRef.nativeElement.contains(event.target as Node))
      )
      .subscribe(() => this.closeDropdown());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.updateSelectionFromModelValue();
      this.updateFilteredOptions();
    }
    if (changes.multiSelect) {
      this.updateSelectionFromModelValue();
    }
  }

  public writeValue(value: any): void {
    this.modelValue = value;
    this.updateSelectionFromModelValue();
  }

  public registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public onDisplayBarClick(event: Event): void {
    event.stopPropagation();
    this.toggleDropdown();
  }

  public toggleDropdown(): void {
    const willOpen = !this.dropdownOpen;

    if (willOpen) {
      if (EpFormSelectDropdownComponent.currentlyOpenDropdown && EpFormSelectDropdownComponent.currentlyOpenDropdown !== this) {
        EpFormSelectDropdownComponent.currentlyOpenDropdown.forceClose();
      }
      this.dropdownOpen = true;
      EpFormSelectDropdownComponent.currentlyOpenDropdown = this;
      this.searchTerm = '';
      this.updateFilteredOptions();
      setTimeout(() => this.searchInputEl?.nativeElement.focus(), 0);
    } else {
      this.closeDropdown();
    }
    this.cdr.markForCheck();
  }

  public closeDropdown(): void {
    if (this.dropdownOpen) {
      this.dropdownOpen = false;
      if (EpFormSelectDropdownComponent.currentlyOpenDropdown === this) {
        EpFormSelectDropdownComponent.currentlyOpenDropdown = null;
      }
      this.onTouched();
      this.cdr.markForCheck();
    }
  }

  public forceClose(): void {
    if (this.dropdownOpen) {
      this.dropdownOpen = false;
      if (EpFormSelectDropdownComponent.currentlyOpenDropdown === this) {
        EpFormSelectDropdownComponent.currentlyOpenDropdown = null;
      }
      this.onTouched();
      this.cdr.markForCheck();
    }
  }

  public onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  public toggleSelection(option: DropdownOption): void {
    const emitValue = this.getEmitValue(option);

    if (this.multiSelect) {
      const index = this.selectedOptions.findIndex((o) => this.compareOptions(o, option));
      index >= 0 ? this.selectedOptions.splice(index, 1) : this.selectedOptions.push(option);
      const newModelValue = this.selectedOptions.map((opt) => this.getEmitValue(opt));
      this.onChange(newModelValue);
      this.selectionChanged.emit(newModelValue);
    } else {
      this.selectedOption = option;
      this.onChange(emitValue);
      this.selectionChanged.emit(emitValue);
      this.closeDropdown();
    }
    this.cdr.markForCheck();
  }

  public onOptionKeydown(event: KeyboardEvent, option: DropdownOption): void {
    event.preventDefault();
    this.toggleSelection(option);
  }

  public isSelected(option: DropdownOption): boolean {
    return this.multiSelect
      ? this.selectedOptions.some((o) => this.compareOptions(o, option))
      : this.selectedOption
      ? this.compareOptions(this.selectedOption, option)
      : false;
  }

  public get getSearchPlaceholder(): string {
    if (this.searchTerm) return this.searchPlaceholder;
    if (this.multiSelect && this.selectedOptions.length) return this.getSelectedLabels();
    if (!this.multiSelect && this.selectedOption) return this.selectedOption[this.labelKey];
    return this.searchPlaceholder;
  }

  public getSelectedLabels(): string {
    return this.multiSelect && this.selectedOptions.length ? this.selectedOptions.map((opt) => opt[this.labelKey] ?? '').join(', ') : '';
  }

  public updateFilteredOptions(): void {
    const term = this.searchTerm?.trim().toLowerCase() || '';
    if (!term) {
      this.filteredOptions = this.options ? [...this.options] : [];
      this.cdr.markForCheck();
      return;
    }
    this.filteredOptions = this.options
      ? this.options.filter((option) => {
          const label = option[this.labelKey]?.toString().toLowerCase();
          return label?.startsWith(term);
        })
      : [];
    this.cdr.markForCheck();
  }

  private updateSelectionFromModelValue(): void {
    const value = this.modelValue;
    if (this.multiSelect) {
      this.selectedOptions =
        Array.isArray(value) && this.options && this.options.length > 0 ? this.options.filter((opt) => value.some((v) => this.compareOptions(opt, v))) : [];
      this.selectedOption = null;
    } else {
      this.selectedOption =
        value != null && this.options && this.options.length > 0 ? this.options.find((opt) => this.compareOptions(opt, value)) || null : null;
      this.selectedOptions = [];
    }
    this.updateFilteredOptions();
    this.cdr.markForCheck();
  }

  private getEmitValue = (opt: DropdownOption | null): any => {
    if (!opt) return null;
    return this.valueKey && opt.hasOwnProperty(this.valueKey) ? opt[this.valueKey] : opt;
  };

  private compareOptions(o1: any, o2: any): boolean {
    if (o1 === o2) return true;
    if (this.valueKey && typeof o1 === 'object' && o1 !== null && o1.hasOwnProperty(this.valueKey)) {
      const val1 = o1[this.valueKey];
      if (typeof o2 === 'object' && o2 !== null && o2.hasOwnProperty(this.valueKey)) {
        return val1 === o2[this.valueKey];
      }
      return val1 === o2;
    }
    return false;
  }
}
